import moment, { type Moment } from "moment-mini";
import type { ErrorProps, EventProps, ExpirationProps, LocationProps, MarketProps, MatchProps, OrderProps, PlayerBalanceProps, TournamentProps, TradeProps } from "../../src/types";
import { MarketComponentHelpers } from "../../src/MarketComponents/api";


export const MarketAppHelpers = {
    filterContests: (events:EventProps[], tournaments:TournamentProps[], matches:MatchProps[], league_ids?:string, team_ids?:string, athlete_ids?:string, active_event?:string) => {
        //Lets check league_ids
        let evs = [ ...events ]
        let tns = [ ...tournaments ]
        let matchs = [ ...matches ]
        if(league_ids && league_ids != 'undefined'){
            //Split the league ids now
            let leagues = league_ids.split(',');
            evs = evs.filter(e => leagues.includes(e.league_id.toString()));
            tns = tns.filter(t => leagues.includes(t.league_id.toString()));
            matchs = matchs.filter(m => tns.map(t => t.tournament_id.toString()).includes(m.tournament_id.toString()));
        }
        if(active_event){ evs = evs.filter(e => e.event_id == active_event) }
        return { events: evs.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix()), tournaments: tns, matches: matchs }
    },

    filterPropTrades: (markets:MarketProps[], event_ids:string[], latest_trades:{ [key:string]: TradeProps[] }, active_event?:string) => {
        let prop_markets = markets.filter(m => m.level == 'athlete' && m.type == 'Stat');
        let prop_trades:TradeProps[] = []
        let event_trades = Object.keys(latest_trades).filter(t => event_ids.map(id => `team:${id}`).includes(t))
        event_trades = [ ...new Set(event_trades.map(t => t)) ]
        event_trades.map(t => { 
            let trades = latest_trades[t] ?? []
            trades.map(t => {
                let market = markets.find(m => m.market_id == t.market_id)
                if(t.market_type == 'FREE' || t.side != market?.trade_side){ return }
                if(!prop_markets.map(m => m.market_id.toString()).includes(t.market_id.toString())){ return }
                prop_trades.push(t);
            })
        });
        if(active_event){ prop_trades = prop_trades.filter(t => t.event_id == active_event) }
        return prop_trades.sort((a,b) => b.cumulative_amt - a.cumulative_amt || b.var_1 - a.var_1).slice(0,12)
    },
    getPropTradesForEvent: (markets:MarketProps[], latest_trades: TradeProps[], active_event:string) => {
        let prop_markets = markets.filter(m => m.level == 'athlete');
        let prop_trades:TradeProps[] = []
        let event_trades = latest_trades.filter(t => t.event_id == active_event);   
        event_trades.map(t => {
            let market = markets.find(m => m.market_id == t.market_id)
            if(t.market_type == 'FREE' || t.side != market?.trade_side){ return }
            if(!prop_markets.map(m => m.market_id.toString()).includes(t.market_id.toString())){ return }
            prop_trades.push(t);
        })
        return prop_trades.sort((a,b) => b.cumulative_amt - a.cumulative_amt || b.var_1 - a.var_1)
    },
    getQuickExpireOptions: (contest_type:'team'|'tournament'|'match', event?:EventProps, tournament?:TournamentProps, match?:MatchProps):ExpirationProps[] => {
        const now = moment();
        let options:ExpirationProps[] = [
            { type: 'increment', increment: 'minutes', increment_value: 5, label: '5 Minutes' },
            { type: 'increment', increment: 'hours', increment_value:1, label:'1 Hour' }
        ]
        switch(contest_type){
            case 'team':
                if(!event){ break }
                if(now.isAfter(moment(event.scheduled_datetime))){ break }
                options.push({ type:'timestamp', label:'Game Start', default:true, expire_datetime: moment(event.scheduled_datetime) })
                break
            case 'tournament':
                if(!tournament){ break }
                if(now.isAfter(moment(tournament.scheduled_datetime))){ break }
                options.push({ type:'timestamp', label:'Tournament Start', default:true, expire_datetime: moment(tournament.scheduled_datetime) })
                break
            case 'match':
                if(!match){ break }
                if(now.isAfter(moment(match.scheduled_datetime))){ break }
                options.push({ type:'timestamp', label:'Match Start', default:true, expire_datetime: moment(match.scheduled_datetime) })
                break
            default: break
        }
        options.push({ type:'timestamp', label:'Custom', expire_datetime: moment().add(15, 'minutes') })
        return options
    },
    getContestDetails: (order:OrderProps, events:EventProps[], tournaments:TournamentProps[], matches:MatchProps[], athletes:AthleteProps[], teams:TeamProps[], leagues:LeagueProps[]):{ title:string, time:string, league?:LeagueProps, athlete?:AthleteProps, team?:TeamProps } => {
        let athlete = order.side_type == 'athlete' ? athletes.find(a => a.athlete_id == order.side_id) : undefined
        let team = order.side_type == 'team' ? teams.find(t => t.team_id == order.side_id) : undefined;
        
        switch(order.event_type){
            case 'team': 
                let event = events.find(e => e.event_id == order.event_id);
                if(!event){ return { team, athlete, title: '', time: '' } }
                let league = leagues.find(l => l.league_id == event.league_id);
                return { team, athlete, league, title: event.event_title, time: event.status == 'scheduled' ? moment(event.scheduled_datetime).format('MM/DD @ hh:mm a') : event.time_detail ?? '' }
            case 'match':
                let match = matches.find(m => m.match_id == order.event_id);
                if(!match){ return { team, athlete, title: '', time: '' } }
                let tourney = tournaments.find(t => t.tournament_id == match.tournament_id);
                let m_league = leagues.find(l => l.league_id == tourney?.league_id)
                return { team, athlete, league: m_league, title: match.match_title, time: moment(match.scheduled_datetime).format('MM/DD @ hh:mm a') }
            case 'tournament':
                let tournament = tournaments.find(t => t.tournament_id == order.event_id);
                if(!tournament){ return { title: '', time: '' } }
                const t_league = leagues.find(l => l.league_id == tournament.league_id)
                return { team, athlete, league: t_league, title: tournament.tournament_name, time: moment(tournament.scheduled_datetime).format('MM/DD @ hh:mm a') }
            default : return { title: '', time: '' }
        }
    },
    isLive: (contest_type:string, event?:EventProps, tournament?:TournamentProps, match?:MatchProps) => {
        switch(contest_type){
            case 'team':
                if(!event){ return false }
                if(moment().isAfter(moment(event.scheduled_datetime))){ return true }
                return false
            case 'tournament':
                if(!tournament){ return false }
                if(moment().isAfter(moment(tournament.scheduled_datetime))){ return true }
                return false
            case 'match':
                if(!match){ return false }
                if(moment().isAfter(moment(match.scheduled_datetime))){ return true }
                return false
            default: return false
        }
    },
    getExpireDatetimeFromOption: (expire_option?:ExpirationProps):Moment => {
        if(!expire_option){ return moment().add(30, 'seconds') } //Immediately expire
        if(expire_option.type == 'increment' && expire_option.increment && expire_option.increment_value){ return moment().add(expire_option.increment_value as any, expire_option.increment) }
        if(expire_option.type == 'timestamp' && !expire_option.expire_datetime){ return moment().add(30, 'seconds') }
        if(expire_option.type == 'timestamp' && expire_option.expire_datetime){ return expire_option.expire_datetime }
        return moment().add(30, 'seconds')
    },
    getDraftOrderData: (order:OrderProps, open_amt:string, commission_pct:number) => {
        let int_amt = parseInt(open_amt);
        let potential_winnings = MarketComponentHelpers.calcPotentialWinnings(int_amt, order.odds);
        let earnings = potential_winnings - int_amt
        let commission = earnings * commission_pct
        let estimated_earnings = earnings - commission
        return { int_open_amt: int_amt, potential_winnings, estimated_earnings }
    },
    isOrderValid:(order:OrderProps, order_type:'limit'|'market'|'h2h', app_mode:'FREE'|'FOR_MONEY', title?:string, market?:MarketProps, my_balance?:PlayerBalanceProps, location?:LocationProps, event?:EventProps, tournament?:TournamentProps, match?:MatchProps, expire_option?:ExpirationProps, h2h_id?:string):ErrorProps[] => {
        let errors:ErrorProps[] = []
        if(!my_balance){ return [{ type: 'AUTH', key:'player_balance', message: 'No balance was provided' }] }
        if(!market){ return [{ type: 'GENERAL', key:'market', message: 'Market cannot be verified' }] }
        //Balance checks
        if(!order.open_amt){ errors.push({ type:'OBJECT', key:'open_amt', message: 'Invalid Quantity' }) }
        if(isNaN(order.open_amt)){ errors.push({ type:'OBJECT', key:'open_amt', message: 'Invalid Quantity' }) }
        if(!order.promo_balance && app_mode == 'FOR_MONEY' && order.open_amt > my_balance.balance){ errors.push({ type: 'OBJECT', key:'open_amt', message: 'Not enough balance' }) }
        if(order.promo_balance && app_mode == 'FOR_MONEY' && order.open_amt > my_balance.promo_balance){ errors.push({ type: 'OBJECT', key:'open_amt', message: 'Not enough promo funds' }) }
        if(market.max_order && order.open_amt > market.max_order){ errors.push({ type:'OBJECT', key:'open_amt', message: 'Amount exceeds the max amount for the market' }) }
        if( app_mode == 'FREE' && order.open_amt > my_balance.free_market_balance){ errors.push({ type: 'OBJECT', key:'open_amt', message: 'Not enough balance' }) }
        if(!title){ errors.push({ type:'OBJECT', key:'title', message: 'Title is not valid' }) }
        if(title && title.toLowerCase().includes('undefined')){ errors.push({ type:'OBJECT', key:'title', message:'Title is not valid' }) }
        if(!market.var_1_required && order.var_1 != 0){ errors.push({ type:'OBJECT',key:'var_1', message:'Var 1 is not valid for a non-var1 market' }) }
        if(market.var_1_required){
            if(order.var_1 % 0.5 !== 0){ errors.push({ type:'OBJECT', key:'var_1', message: 'Var 1 is not valid' }) }
            if(isNaN(order.var_1)){ errors.push({ type:'OBJECT', key:'var_1', message:'Var 1 is not valid' }) }
            if(market.type != 'Spread'){
                if(order.var_1 <= 0){ errors.push({ type:'OBJECT', key: 'var_1', message:'Var 1 is not valid' }) }
            }
        }
        if(!expire_option){
            errors.push({ type:'OBJECT', key:'expiration_datetime', message: 'Please select an expiration time' })
        }
        if(expire_option?.expire_datetime){
            if(moment().isAfter(expire_option.expire_datetime)){ errors.push({ type:'OBJECT', key:'expiration_datetime', message:'Expire time is set in the past and is not valid' }) }
        }

        if(order_type == 'h2h' && !h2h_id){ errors.push({ type:'OBJECT', key:'h2h_id', message: 'H2H Order must include a player' }) }

        //Object checks
        if(!order.title || order.title.toLowerCase().includes('undefined')){ errors.push({ type: 'OBJECT', key:'title', message: 'Invalid outcome title' }) }
        if(order.open_amt < 1){ errors.push({ type:'OBJECT', key: 'open_amt', message: 'Amount must be greater than $1' }) }

        if(order.probability > .999 || order.probability < 0.00001){ errors.push({ type: "OBJECT", key:'probability', message: 'Probability is invalid' }) }

        //Ok! now lets check if the market is still active!
        switch(order.event_type){
            case 'team':
                if(!event){ 
                    errors.push({ type: 'OBJECT', key:'event_id', message: 'Order cannot be confirmed without the event data' }) 
                    return errors
                }
                if(MarketAppHelpers.getFinalizedStatuses().includes(event.status)){
                    errors.push({ type: 'OBJECT', key:'event_id', message: 'This event has closed and is no longer accepting orders' })
                }
                //grab the supporting markets
                if(!event.supported_markets){
                    errors.push({ type: 'OBJECT', key:'market_id', message: 'This event has no active supported markets' }) 
                    return errors
                }
                let e_supported_market = event.supported_markets.find(sm => sm.market_id == order.market_id);
                if(!e_supported_market || e_supported_market.removed){ errors.push({ type:'OBJECT', key:'market_id', message:'This market is currently no longer accepting orders' }) }
                break
            case 'tournament':
                    if(!tournament){ 
                        errors.push({ type: 'OBJECT', key:'event_id', message: 'Order cannot be confirmed without the event data' }) 
                        return errors
                    }
                    if(MarketAppHelpers.getFinalizedStatuses().includes(tournament.status)){
                        errors.push({ type: 'OBJECT', key:'event_id', message: 'This event has closed and is no longer accepting orders' })
                    }
                    //grab the supporting markets
                    if(!tournament.supported_markets){
                        errors.push({ type: 'OBJECT', key:'market_id', message: 'This event has no active supported markets' }) 
                        return errors
                    }
                    let t_supported_market = tournament.supported_markets.find(sm => sm.market_id == order.market_id);
                    if(!t_supported_market || t_supported_market.removed){ errors.push({ type:'OBJECT', key:'market_id', message:'This market is currently no longer accepting orders' }) }
                    break
                case 'match':
                    if(!match){ 
                        errors.push({ type: 'OBJECT', key:'event_id', message: 'Order cannot be confirmed without the event data' }) 
                        return errors
                    }
                    if(MarketAppHelpers.getFinalizedStatuses().includes(match.status)){
                        errors.push({ type: 'OBJECT', key:'event_id', message: 'This event has closed and is no longer accepting orders' })
                    }
                    //grab the supporting markets
                    if(!match.supported_markets){
                        errors.push({ type: 'OBJECT', key:'market_id', message: 'This event has no active supported markets' }) 
                        return errors
                    }
                    let m_supported_market = match.supported_markets.find(sm => sm.market_id == order.market_id);
                    if(!m_supported_market || m_supported_market.removed){ errors.push({ type:'OBJECT', key:'market_id', message:'This market is currently no longer accepting orders' }) }
                    break
            default: 
                    errors.push({ type:'OBJECT', key:'event_id', message: 'This event type is not supported' });
                    break
        }

        //Location Checks
        if(!location){ errors.push({ type: 'GENERAL', key:'location', message: 'Location must be enabled to submit orders' }) }
        if(app_mode == 'FOR_MONEY' && !location?.legal_ind){ errors.push({ type: 'GENERAL', key:'region', message: `Real money orders are currently not supported in ${location?.label??'your current location'}` }) }
        return errors
    },
    getFinalizedStatuses:() => {
            return [
                'complete',
                'closed',
                'suspended',
                'cancelled',
                'canceled',
                'not_necessary'
            ]
    },
    ContestHelpers: {
        getUniqueDatesFromEvents: (events:EventProps[]):string[] => {
            const unique_dates = [ ...new Set(events.map(e => moment(e.scheduled_datetime).format('YYYY-MM-DD'))) ].sort((a,b) => moment(a).unix() - moment(b).unix())
            return unique_dates.sort((a,b) => moment(a).unix() - moment(b).unix())
        },
        getClosestDay: (schedule_range:string[]):string => {
            let closest:any|undefined;
            let exact_day = schedule_range.find(d => moment(d).format('YYYY-MM-DD') == moment().format('YYYY-MM-DD'))
            if(exact_day){ return moment(exact_day).format('YYYY-MM-DD') }
            schedule_range.map(d => {
                if(!closest){ closest = moment(d) }
                if(moment(d).isBefore(moment())){ return } //Day is in the past
                let new_range = moment(d).diff(moment(), 'days')
                let closest_range = closest.diff(moment(), 'days')
                if(Math.abs(new_range) < Math.abs(closest_range)){ closest = moment(d) }
            })
            return closest.format('YYYY-MM-DD')
        },
        getUniqueTimesFromEvents: (events:EventProps[]) => {
            const unique_times = [ ...new Set(events.map(e => moment(e.scheduled_datetime).format('h:mm a'))) ]
            return unique_times.sort((a,b) => moment(a, 'h:mm a').unix() - moment(b, 'h:mm a').unix())
        },
        getUniqueDivisions: (events:EventProps[]):string[] => {
            let away_divisions = events.map(e => e.away?.division ?? '')
            let home_divisions = events.map(e => e.home?.division ?? '')
            let divisions = away_divisions.concat(home_divisions)
            let options = [ ...new Set(divisions.map(d => d)) ].sort((a,b) => a > b ? 1 : -1)
            let ranks = events.find(e => e.away?.rank || e.home?.rank)
            if(ranks){ options = ['Top25'].concat(options) }
            return options
        },
        getUniqueSubDivisions: (events:EventProps[]):string[] => {    
            let away_divisions = events.map(e => e.away?.sub_division ?? '')
            let home_divisions = events.map(e => e.home?.sub_division ?? '')
            return [ ...new Set(away_divisions.concat(home_divisions).map(d => d)) ].sort((a,b) => a > b ? 1 : -1)
        },
        getFinalizedStatuses:() => {
            return [
                'complete',
                'closed',
                'suspended',
                'cancelled',
                'canceled',
                'not_necessary'
            ]
        },
        FINALIZED_STATUSES: [
            'closed',
            'complete',
            'cancelled',
            'unnecessary',
            'canceled',
            'postponed',
            'suspended'
        ],
        IN_PROGRESS_STATUSES: [
            'inprogress',
            'halftime',
            'intermission'
        ]
    },
}

